import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { resolveLearnRedirect } from './learn-redirect-map';

/**
 * #51 — Guards the legacy `/learn/:group/:slug` Prismic route so a CHANGED handle 301-equivalents to
 * its new Payload segment, while every UNCHANGED `/learn/...` URL keeps resolving via the existing
 * Prismic fallback (no redirect, exact path preserved).
 *
 * Behaviour:
 *  - Look the legacy path up in {@link LEARN_REDIRECTS} (HITL — empty by default).
 *  - On a match: a SINGLE `Router.navigate(..., { replaceUrl: true })` to the final destination and
 *    cancel the legacy activation (return false). `replaceUrl` makes it a clean client-side 301
 *    equivalent (the legacy entry is replaced, not stacked). Because the map values are final
 *    destinations (never another mapped key) there is no chain.
 *  - On no match: allow the legacy route (return true) — the URL is unchanged and still resolves.
 *
 * NOTE: this is the SPA-side redirect. The durable, crawler-facing 301 is an edge/server rule; this
 * guard mirrors the SAME map so the behaviour is consistent in-app and the map is the single source
 * of truth the owner signs off on (see learn-redirect-map.ts header).
 */
@Injectable({ providedIn: 'root' })
class LearnRedirectGuardService {
  constructor(private router: Router) {}

  canActivate(route: ActivatedRouteSnapshot): boolean {
    const group = route.paramMap.get('group');
    const slug = route.paramMap.get('slug');
    if (!group || !slug) {
      return true;
    }

    const target = resolveLearnRedirect(`learn/${group}/${slug}`);
    if (!target) {
      // Unchanged URL — let the Prismic fallback render it (URL preserved, no redirect).
      return true;
    }

    // Changed handle — single hop to the final destination, replacing the legacy entry.
    this.router.navigateByUrl(target, { replaceUrl: true });
    return false;
  }
}

export const LearnRedirectGuard: CanActivateFn = (route: ActivatedRouteSnapshot): boolean =>
  inject(LearnRedirectGuardService).canActivate(route);
